import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  ScrollArea,
  Separator,
  Spinner,
  Switch,
  Text,
} from "@salestrak/ui";

import {
  cellEditable,
  cellGrants,
  SYSTEM_ROLE_DESCRIPTIONS,
  SYSTEM_ROLE_NAMES,
  type CellState,
  type SystemRole,
} from "@salestrak/permissions";
import { NewRoleDialog } from "#app/components/settings/new-role-dialog";
import {
  DeleteRoleDialog,
  RenameRoleDialog,
} from "#app/components/settings/role-actions-dialogs";
import { PageHeader, PageHeaderSubtitle } from "#app/components/page-header";
import { useCustomRoleActions } from "#app/lib/hooks/use-custom-role-actions";
import { useInviteGrantMatrix } from "#app/lib/hooks/use-invite-grant-matrix";
import { usePermissionMatrix } from "#app/lib/hooks/use-permission-matrix";
import { useWorkspaceAdvancedPermissions } from "#app/lib/hooks/use-workspace-advanced-permissions";
import { useWorkspace } from "#app/lib/data/app-provider";
import type { PermissionMatrixRole } from "#app/lib/data";
import { withPermission } from "#app/components/permissions/require-permission";

function SettingsPermissionsPage() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  const { loading, matrix, toggle } = useInviteGrantMatrix(workspaceId);

  // Full role list (system + custom) for the delete-role reassignment
  // picker. Apollo dedupes this against the same query inside
  // CustomRolePermissionsSection, so it's not a second network round-trip.
  const { matrix: permMatrix } = usePermissionMatrix(workspaceId);
  const allRoles = useMemo(() => permMatrix?.roles ?? [], [permMatrix]);

  // Custom-role rename / delete — shared across both matrices' column
  // header menus, so the dialog state + mutations live at page level.
  const { renameRole, deleteRole } = useCustomRoleActions(workspaceId);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const handleRenameRole = useCallback(
    (role: { id: string; roleName: string }) =>
      setRenameTarget({ id: role.id, name: role.roleName }),
    [],
  );
  const handleDeleteRole = useCallback(
    (role: { id: string; roleName: string }) =>
      setDeleteTarget({ id: role.id, name: role.roleName }),
    [],
  );

  // The API returns the roles worth showing per axis: inviterRoles are the
  // columns, invitableRoles the rows. (Owner is dropped from both; Admin is
  // a column but not a row, since Owner above it is fully locked.)
  const inviterRoles = matrix?.inviterRoles ?? [];
  const invitableRoles = matrix?.invitableRoles ?? [];

  // `${inviterId}:${invitableId}` → cell state, straight from the DB.
  const stateByCell = useMemo(() => {
    const map = new Map<string, CellState>();
    for (const g of matrix?.grants ?? []) {
      map.set(`${g.inviterRoleId}:${g.invitableRoleId}`, g.state as CellState);
    }
    return map;
  }, [matrix]);

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Permissions"
        subtitle={
          <PageHeaderSubtitle>
            Manage roles and access controls for your workspace.
          </PageHeaderSubtitle>
        }
      />
      <Separator />
      <ScrollArea>
        <Box padding="lg" gap="lg">
          <RolesSection />
          <Separator />
          <Box direction="row" align="center" gap="sm">
            <Icon name="user" size="xl" color="muted" />
            <Text size="lg" weight="semibold">
              Invite Permissions
            </Text>
          </Box>
          {loading && !matrix ? (
            <Box align="center" justify="center" padding="lg">
              <Spinner />
            </Box>
          ) : inviterRoles.length === 0 ? (
            <Text variant="muted">No roles found.</Text>
          ) : (
            <InviteGrantMatrix
              inviterRoles={inviterRoles}
              invitableRoles={invitableRoles}
              stateByCell={stateByCell}
              onToggle={toggle}
              onRenameRole={handleRenameRole}
              onDeleteRole={handleDeleteRole}
            />
          )}
          <Separator />
          <CustomRolePermissionsSection
            workspaceId={workspaceId}
            onRenameRole={handleRenameRole}
            onDeleteRole={handleDeleteRole}
          />
          <Separator />
          <AdvancedPermissionsSection workspaceId={workspaceId} />
        </Box>
      </ScrollArea>
      <RenameRoleDialog
        open={renameTarget != null}
        onOpenChange={(next) => {
          if (!next) setRenameTarget(null);
        }}
        currentName={renameTarget?.name ?? ""}
        onConfirm={async (nextName) => {
          if (renameTarget) await renameRole(renameTarget.id, nextName);
        }}
      />
      <DeleteRoleDialog
        open={deleteTarget != null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        roleName={deleteTarget?.name ?? ""}
        reassignRoles={allRoles.filter((r) => r.id !== deleteTarget?.id)}
        onConfirm={async (reassignToRoleId) => {
          if (deleteTarget) await deleteRole(deleteTarget.id, reassignToRoleId);
        }}
      />
    </Box>
  );
}

// Shared column-header cell for both matrices. System roles render as
// plain text; custom roles get the chevron menu with Rename / Delete.
function RoleColumnHeader({
  role,
  onRename,
  onDelete,
}: {
  role: PermissionMatrixRole;
  onRename: (role: { id: string; roleName: string }) => void;
  onDelete: (role: { id: string; roleName: string }) => void;
}) {
  if (role.isSystem) {
    return (
      <Text size="xs" variant="muted" weight="semibold">
        {role.roleName.toUpperCase()}
      </Text>
    );
  }
  return (
    <Menu>
      <MenuTrigger
        render={
          <Box
            as="button"
            direction="row"
            align="center"
            gap="2xs"
            rounded="sm"
            paddingX="2xs"
            hoverBackground
          />
        }
      >
        <Text size="xs" variant="muted" weight="semibold">
          {role.roleName.toUpperCase()}
        </Text>
        <Icon name="chevron-down" size="xs" color="muted" />
      </MenuTrigger>
      <MenuPopup align="end" className="min-w-40">
        <MenuItem onClick={() => onRename(role)}>
          <Icon name="pen-line" size="sm" color="muted" />
          Rename role
        </MenuItem>
        <MenuItem variant="destructive" onClick={() => onDelete(role)}>
          <Icon name="trash-2" size="sm" />
          Delete role
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

// ─── Advanced Permissions ─────────────────────────────────────────────
// Two workspace-level toggles shown beneath the role / invite
// matrices. Reads + writes go through useWorkspaceAdvancedPermissions
// so Apollo stays out of this component file.
function AdvancedPermissionsSection({ workspaceId }: { workspaceId: string }) {
  const {
    loading,
    ready,
    defaultPrivateWorkflows,
    adminOverrideEnabled,
    setField,
  } = useWorkspaceAdvancedPermissions(workspaceId);

  return (
    <Box gap="md">
      <Box direction="row" align="center" gap="sm">
        <Icon name="lock" size="xl" color="muted" />
        <Text size="lg" weight="semibold">
          Advanced Permissions
        </Text>
      </Box>
      {loading && !ready ? (
        <Box align="center" justify="center" padding="lg">
          <Spinner />
        </Box>
      ) : (
        <Box gap="none">
          <AdvancedPermissionsRow
            icon="folder-kanban"
            title="Make new Workflows private"
            description="Default all newly created Workflows to private."
            value={defaultPrivateWorkflows}
            onChange={(next) => setField("defaultPrivateWorkflows", next)}
          />
          <Separator />
          <AdvancedPermissionsRow
            icon="folder-kanban"
            title="Allow admins to manage anything private"
            description="Gives admins the ability to add and remove people, and transfer ownership of any private Workflow, Project, or View."
            value={adminOverrideEnabled}
            onChange={(next) => setField("adminOverrideEnabled", next)}
          />
        </Box>
      )}
    </Box>
  );
}

function AdvancedPermissionsRow({
  icon,
  title,
  description,
  value,
  onChange,
}: {
  icon: "folder-kanban";
  title: string;
  description: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <Box direction="row" align="center" gap="md" paddingY="md">
      <Switch checked={value} onCheckedChange={onChange} />
      <Box flex={1} gap="2xs">
        <Box direction="row" align="center" gap="xs">
          <Icon name={icon} size="sm" color="muted" />
          <Text size="sm" weight="semibold">
            {title}
          </Text>
        </Box>
        <Text size="sm" variant="muted">
          {description}
        </Text>
      </Box>
    </Box>
  );
}

// System roles shown in the "Roles" header strip. Owner is excluded —
// it's an all-access role and isn't shown alongside the editable tiers.
const ROLE_BLURB_ORDER: readonly SystemRole[] = [
  "guest",
  "limited_member",
  "member",
  "admin",
];

function RolesSection() {
  return (
    <Box gap="md">
      <Box direction="row" align="center" gap="sm">
        <Icon name="shield" size="xl" color="muted" />
        <Text size="lg" weight="semibold">
          Roles
        </Text>
      </Box>
      <Box
        direction="row"
        border
        rounded="lg"
        surface="card"
        overflow="hidden"
      >
        {ROLE_BLURB_ORDER.map((role, i) => (
          <Box
            key={`${role}-${i}`}
            fill="flex"
            padding="md"
            gap="2xs"
            border={i === 0 ? undefined : "left"}
          >
            <Text size="sm" weight="semibold">
              {SYSTEM_ROLE_NAMES[role]}
            </Text>
            <Text size="xs" variant="muted">
              {SYSTEM_ROLE_DESCRIPTIONS[role]}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const COL_WIDTH = 140;

// Rows = the role being invited ("Invite Members"). Columns = the role
// doing the inviting. Each cell renders its DB cell state:
//   no_locked  → × (system-imposed off, e.g. hierarchy ceiling / guests)
//   yes_locked → ✓ (system-imposed on, e.g. admins always can)
//   yes / no   → editable Switch
function InviteGrantMatrix({
  inviterRoles,
  invitableRoles,
  stateByCell,
  onToggle,
  onRenameRole,
  onDeleteRole,
}: {
  inviterRoles: readonly PermissionMatrixRole[];
  invitableRoles: readonly PermissionMatrixRole[];
  stateByCell: Map<string, CellState>;
  onToggle: (inviterRoleId: string, invitableRoleId: string, granted: boolean) => void;
  onRenameRole: (role: { id: string; roleName: string }) => void;
  onDeleteRole: (role: { id: string; roleName: string }) => void;
}) {
  return (
    <Box border rounded="lg" surface="card" overflow="hidden">
      {/* Header */}
      {/* One tonal step below the card (`muted` fill, no re-declared surface):
          re-naming `card` here would repaint the container's own fill and
          stack a second card shadow, and the hard hairline under it read as a
          heavy table seam. */}
      <Box direction="row" background="muted">
        <Box fill="flex" paddingX="md" paddingY="sm" justify="center">
          <Text size="xs" variant="muted" weight="semibold">
            ACTIONS
          </Text>
        </Box>
        {inviterRoles.map((inviter, i) => (
          <Box
            key={`${inviter.id}-${i}`}
            width={COL_WIDTH}
            paddingX="md"
            paddingY="sm"
            align="center"
            justify="center"
            border="left"
          >
            <RoleColumnHeader
              role={inviter}
              onRename={onRenameRole}
              onDelete={onDeleteRole}
            />
          </Box>
        ))}
      </Box>

      {/* Rows: one per invitable target role. No horizontal dividers
          between rows — only vertical dividers between columns. Cells
          stretch to the full row height (default align) so the column
          borders form continuous vertical lines with no gaps. */}
      {invitableRoles.map((target, ri) => (
        <Box key={`${target.id}-${ri}`} direction="row" hoverBackground>
          <Box fill="flex" paddingX="md" paddingY="md" gap="2xs">
            <Text size="sm" weight="semibold">
              Invite {target.roleName}
            </Text>
            <Text size="xs" variant="muted">
              Allow granting new members the {target.roleName} role on invite.
            </Text>
          </Box>
          {inviterRoles.map((inviter, ci) => {
            const state = stateByCell.get(`${inviter.id}:${target.id}`) ?? "no";
            return (
              <Box
                key={`${inviter.id}-${ci}`}
                width={COL_WIDTH}
                paddingX="md"
                paddingY="md"
                align="center"
                justify="center"
                border="left"
              >
                {!cellEditable(state) ? (
                  cellGrants(state) ? (
                    <Icon name="check" size="sm" color="muted" />
                  ) : (
                    <Icon name="x" size="sm" color="muted" />
                  )
                ) : (
                  <Switch
                    checked={cellGrants(state)}
                    onCheckedChange={(next) =>
                      onToggle(inviter.id, target.id, next)
                    }
                  />
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ─── Custom Role Permissions ──────────────────────────────────────────
// Role × permission-key matrix. Rows are grouped by registry category so
// the table reads like a settings page rather than a flat grid. Pulls
// `permissionMatrix` and writes via `setRolePermission` — both already
// guarded by `create.roles` on the resolver.

// Empty string = render the group without a section header row. The
// "create_delete" bucket (Create / Delete / Archive Items) shows up
// inline like a continuation of the section above it.
const CATEGORY_LABELS: Record<string, string> = {
  manage: "MANAGE",
  workflows: "WORKFLOWS",
  create_delete: "",
  custom_fields: "CUSTOM FIELDS",
  comments: "COMMENTS",
  collaboration: "COLLABORATION",
};

// Order categories in the display the same way the registry lists them
// rather than alphabetically. `workspace`-category keys (issue_types /
// reasons / trash manage) are deliberately absent — backend-only for
// now, not editable from the matrix UI.
const CATEGORY_ORDER: readonly string[] = [
  "manage",
  "custom_fields",
  "workflows",
  "create_delete",
  "comments",
  "collaboration",
];

// Show Owner-less columns to match the invite matrix (Owner is all-locked
// yes and adds no decision).
const HIDDEN_ROLE_NAMES = new Set([SYSTEM_ROLE_NAMES.owner]);

function CustomRolePermissionsSection({
  workspaceId,
  onRenameRole,
  onDeleteRole,
}: {
  workspaceId: string;
  onRenameRole: (role: { id: string; roleName: string }) => void;
  onDeleteRole: (role: { id: string; roleName: string }) => void;
}) {
  const { loading, matrix, setCell } = usePermissionMatrix(workspaceId);
  const [search, setSearch] = useState("");
  const [showNewRole, setShowNewRole] = useState(false);

  const roles = useMemo(
    () =>
      (matrix?.roles ?? []).filter(
        (r) => !HIDDEN_ROLE_NAMES.has(r.roleName),
      ),
    [matrix],
  );
  const keys = useMemo(() => matrix?.keys ?? [], [matrix]);

  // `${roleId}:${permissionKey}` → cell state.
  const stateByCell = useMemo(() => {
    const map = new Map<string, CellState>();
    for (const c of matrix?.cells ?? []) {
      map.set(`${c.roleId}:${c.permissionKey}`, c.state as CellState);
    }
    return map;
  }, [matrix]);

  // Substring filter against label + description so the admin can
  // narrow the matrix to a single rule without scrolling. Case-
  // insensitive; empty query keeps the full grouped list.
  const filteredKeys = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter((k) => {
      const label = (k.label ?? "").toLowerCase();
      const desc = (k.description ?? "").toLowerCase();
      return label.includes(q) || desc.includes(q);
    });
  }, [keys, search]);

  const keysByCategory = useMemo(() => {
    const grouped = new Map<string, typeof filteredKeys>();
    for (const k of filteredKeys) {
      const arr = grouped.get(k.category) ?? [];
      arr.push(k);
      grouped.set(k.category, arr);
    }
    return CATEGORY_ORDER.filter((c) => grouped.has(c)).map((c) => ({
      category: c,
      keys: grouped.get(c)!,
    }));
  }, [filteredKeys]);

  return (
    <Box gap="md">
      <Box direction="row" align="center" gap="sm">
        <Icon name="lock" size="xl" color="muted" />
        <Text size="lg" weight="semibold">
          Custom Role Permissions
        </Text>
      </Box>

      {/* Toolbar — search filters the matrix in place; "+ New role"
          opens the dialog. Always render so the user can start typing
          before the matrix finishes loading. */}
      <Box direction="row" align="center" gap="sm">
        <Box flex={1}>
          <InputGroup>
            <InputGroupAddon>
              <Icon name="search" />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              placeholder="Search for action..."
              value={search}
              onValueChange={setSearch}
            />
          </InputGroup>
        </Box>
        <Button variant="outline" onClick={() => setShowNewRole(true)}>
          <Icon name="plus" size="sm" />
          <Text>New Role</Text>
        </Button>
      </Box>

      {loading && !matrix ? (
        <Box align="center" justify="center" padding="lg">
          <Spinner />
        </Box>
      ) : roles.length === 0 || keys.length === 0 ? (
        <Text variant="muted">No roles found.</Text>
      ) : (
        <Box border rounded="lg" surface="card" overflow="hidden">
          {/* Header */}
          {/* One tonal step below the card (`muted` fill, no re-declared surface):
          re-naming `card` here would repaint the container's own fill and
          stack a second card shadow, and the hard hairline under it read as a
          heavy table seam. */}
      <Box direction="row" background="muted">
            <Box fill="flex" paddingX="md" paddingY="sm" justify="center">
              <Text size="xs" variant="muted" weight="semibold">
                ACTIONS
              </Text>
            </Box>
            {roles.map((role, i) => (
              <Box
                key={`${role.id}-${i}`}
                width={COL_WIDTH}
                paddingX="md"
                paddingY="sm"
                align="center"
                justify="center"
                border="left"
              >
                <RoleColumnHeader
                  role={role}
                  onRename={onRenameRole}
                  onDelete={onDeleteRole}
                />
              </Box>
            ))}
          </Box>

          {keysByCategory.map((group, gi) => (
            <Box
              key={`${group.category}-${gi}`}
              border={gi < keysByCategory.length - 1 ? "bottom" : undefined}
            >
              {/* Category header — subtle background tint, no divider
                  beneath so it reads as a label sitting above its rows.
                  Skipped entirely when the label is empty (the bucket
                  flows inline with the section above). */}
              {gi > 0 && (CATEGORY_LABELS[group.category] ?? "").length > 0 ? (
                <Box direction="row">
                  <Box fill="flex" paddingX="md" paddingY="lg">
                    <Text size="xs" variant="muted" weight="semibold">
                      {CATEGORY_LABELS[group.category] ??
                        group.category.toUpperCase()}
                    </Text>
                  </Box>
                  {roles.map((role, ci) => (
                    <Box
                      key={`${role.id}-cat-${ci}`}
                      width={COL_WIDTH}
                      border="left"
                    />
                  ))}
                </Box>
              ) : null}
              {group.keys.map((perm, pi) => (
                <Box
                  key={`${perm.key}-${pi}`}
                  direction="row"
                  hoverBackground
                >
                  <Box fill="flex" paddingX="md" paddingY="md" gap="2xs">
                    <Text size="sm" weight="semibold">
                      {perm.label}
                    </Text>
                    <Text size="xs" variant="muted">
                      {perm.description}
                    </Text>
                  </Box>
                  {roles.map((role, ci) => {
                    const state =
                      stateByCell.get(`${role.id}:${perm.key}`) ?? "no";
                    const editable = cellEditable(state);
                    // Toggle reads as "on" for any granting state, including
                    // if_created (which grants conditionally on ownership).
                    const granted =
                      state === "yes" ||
                      state === "yes_locked" ||
                      state === "if_created";
                    const showIfCreated =
                      !!perm.supportsOnlyIfCreated && editable && granted;
                    return (
                      <Box
                        key={`${role.id}-${ci}`}
                        width={COL_WIDTH}
                        paddingX="md"
                        paddingY="md"
                        align="center"
                        justify="center"
                        gap="xs"
                        border="left"
                      >
                        {!editable ? (
                          granted ? (
                            <Icon name="check" size="sm" color="muted" />
                          ) : (
                            <Icon name="x" size="sm" color="muted" />
                          )
                        ) : (
                          <Switch
                            checked={granted}
                            onCheckedChange={(next) =>
                              setCell(
                                role.id,
                                perm.key,
                                next
                                  ? state === "if_created"
                                    ? "if_created"
                                    : "yes"
                                  : "no",
                              )
                            }
                          />
                        )}
                        {showIfCreated ? (
                          // Checkbox has no label slot; wrap it in a label so
                          // the text is part of the hit target.
                          <Box
                            as="label"
                            direction="row"
                            align="center"
                            gap="xs"
                            cursor="pointer"
                          >
                            <Checkbox
                              checked={state === "if_created"}
                              onCheckedChange={(next) =>
                                setCell(
                                  role.id,
                                  perm.key,
                                  next ? "if_created" : "yes",
                                )
                              }
                            />
                            <Text size="xs" variant="muted" truncate={1}>
                              Only if created
                            </Text>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
      <NewRoleDialog
        open={showNewRole}
        onOpenChange={setShowNewRole}
        workspaceId={workspaceId}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/permissions")({
  component: withPermission("create.roles", SettingsPermissionsPage),
});
